// src/components/events/ManageRecipientsCard.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkHeading from '../common/SparkHeading';
import useEventPaywall from '../../hooks/useEventPaywall';

interface ResponseData {
  accepted: number;
  declined: number;
  pending: number;
  totalPeople: number;
  anonymous: {
    views: number;
    accepted: number;
    declined: number;
    people: number;
  };
}

interface Invitation {
  id: string;
  recipientName: string;
  status: string;
  sentAt?: { seconds: number };
  peopleCount?: number;
}

interface ManageRecipientsCardProps {
  eventId: string;
  recipientCount: number;
  responseData?: ResponseData;
}

const ManageRecipientsCard: React.FC<ManageRecipientsCardProps> = ({
  eventId,
  recipientCount,
  responseData
}) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'personal' | 'link'>('summary');
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationLink, setInvitationLink] = useState<string>('');
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  // Paywall status + anonymous link from backend
  const { unlocked, anonymousLink } = useEventPaywall(eventId);

  // Default sample when responseData not provided
  const responses: ResponseData = responseData || {
    accepted: 12,
    declined: 3,
    pending: 8,
    totalPeople: 24,
    anonymous: { views: 45, accepted: 5, declined: 2, people: 10 }
  };

  // Personal invitations
  useEffect(() => {
    if (!eventId) return;
    const fetchInvitations = async () => {
      try {
        const invitationsRef = collection(db, 'invitations');
        const q = query(
          invitationsRef,
          where('eventId', '==', eventId),
          where('recipientName', '!=', 'Anonymous')
        );
        const snapshot = await getDocs(q);
        const invitationData = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Invitation, 'id'>)
        }));
        setInvitations(invitationData);
      } catch (error) {
        console.error('Error fetching invitations:', error);
      }
    };
    fetchInvitations();
  }, [eventId]);

  // Anonymous link with safe fallback on current origin
  useEffect(() => {
    if (anonymousLink) {
      setInvitationLink(anonymousLink);
    } else if (unlocked) {
      setInvitationLink(`${window.location.origin}/invite/${eventId}?id=${eventId}`);
    } else {
      setInvitationLink('');
    }
  }, [anonymousLink, unlocked, eventId]);

  const handleSendNew = () => {
    if (!unlocked) {
      // Trigger the global unlock modal
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
      return;
    }
    window.location.href = `/create-event?eventId=${eventId}&step=8`;
  };

  const handleResendInvitation = async (invitationId: string) => {
    if (!unlocked) {
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
      return;
    }
    console.log('Resending invitation:', invitationId);
    // TODO: call backend function if you choose to implement
  };

  const handleCopyLink = async () => {
    if (!unlocked) {
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
      return;
    }
    
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(invitationLink);
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = invitationLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
      }
    } catch (error) {
      console.error('Error copying link:', error);
      alert('Failed to copy link. Please try again.');
    }
  };

  const handleShare = async () => {
    if (!unlocked) {
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
      return;
    }
    
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share({ 
          title: "You're Invited!", 
          text: 'Check out this event!', 
          url: invitationLink 
        });
      } else {
        await handleCopyLink();
      }
    } catch (error) {
      console.error('Error sharing link:', error);
    }
  };

  return (
    <SparkCard variant="solid" className="manage-recipients-card" style={{ width: '100%' }}>
      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}
      >
        <SparkHeading level="h3" style={{ color: '#000', textShadow: 'none', marginBottom: 0 }}>
          Guests & Responses
        </SparkHeading>

        <select
          id="tabSelect"
          className="spark-input"
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value as any)}
          style={{
            minWidth: '150px',
            height: '3rem',
            fontWeight: 'bold',
            boxSizing: 'border-box',
            padding: '0 1rem',
            lineHeight: '3rem'
          }}
        >
          <option value="summary">Summary</option>
          <option value="personal">Personal Invites</option>
          <option value="link">Link Sharing</option>
        </select>
      </div>

      <div
        style={{
          border: '4px solid black',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          backgroundColor: 'white'
        }}
      >
        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div style={{ padding: '1rem' }}>
            <SparkHeading level="h4" style={{ color: '#000', textShadow: 'none', marginBottom: '0.75rem' }}>
              Responses
            </SparkHeading>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[
                { label: 'Accepted', value: responses.accepted + responses.anonymous.accepted, color: 'var(--color-success)' },
                { label: 'Declined', value: responses.declined + responses.anonymous.declined, color: 'var(--color-error)' },
                { label: 'Pending', value: responses.pending, color: 'var(--color-accent-yellow)' },
                { label: 'People', value: responses.totalPeople, color: 'var(--color-accent-violet)' },
                { label: 'Anonymous Views', value: responses.anonymous.views, color: 'var(--color-accent-violet)' }
              ].map(({ label, value, color }, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderBottom: i < 4 ? '2px dashed #d1d5db' : undefined,
                    padding: i < 4 ? '0.25rem 0' : '0.25rem 0 0'
                  }}
                >
                  <span style={{ fontWeight: 'bold', color: '#000' }}>{label}:</span>
                  <span style={{ color, fontWeight: 'bold' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Personal Invites Tab */}
        {activeTab === 'personal' && (
          <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <SparkHeading level="h4" style={{ color: '#000', textShadow: 'none', marginBottom: 0 }}>
                Personal Invitations
              </SparkHeading>

              <SparkButton size="small" onClick={handleSendNew}>
                {unlocked ? 'SEND NEW' : '🔒 UNLOCK TO SEND'}
              </SparkButton>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="spark-table">
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid black' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#000' }}>Guest</th>
                    <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', color: '#000' }}>Status</th>
                    <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', color: '#000' }}>People</th>
                    <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', color: '#000' }}>Sent</th>
                    <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', color: '#000' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.length > 0 ? (
                    invitations.map((inv) => (
                      <tr key={inv.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.5rem 0.75rem', fontWeight: 'bold', color: '#000' }}>
                          {inv.recipientName}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              backgroundColor:
                                inv.status === 'accepted'
                                  ? 'var(--color-success)'
                                  : inv.status === 'declined'
                                  ? 'var(--color-error)'
                                  : inv.status === 'viewed'
                                  ? '#3b82f6'
                                  : 'var(--color-accent-yellow)',
                              color: 'white'
                            }}
                          >
                            {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 'bold' }}>
                          {inv.peopleCount || 1}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontSize: '0.875rem' }}>
                          {inv.sentAt ? new Date(inv.sentAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                          <button
                            style={{
                              color: unlocked ? '#3b82f6' : '#999',
                              textDecoration: 'underline',
                              fontSize: '0.875rem',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: unlocked ? 'pointer' : 'not-allowed'
                            }}
                            onClick={() => handleResendInvitation(inv.id)}
                            disabled={!unlocked}
                          >
                            {unlocked ? 'Resend' : '🔒'}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: '#000' }}>
                        No invitations sent yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <Link
                to={`/event/${eventId}/guests`}
                style={{ color: 'var(--color-accent-violet)', fontWeight: 'bold', textDecoration: 'none' }}
              >
                View all guests →
              </Link>
            </div>
          </div>
        )}

        {/* Link Sharing Tab */}
        {activeTab === 'link' && (
          <div style={{ padding: '1rem' }}>
            <SparkHeading level="h4" style={{ color: '#000', textShadow: 'none', marginBottom: '0.75rem' }}>
              Shareable Invitation Link
            </SparkHeading>

            {!unlocked && (
              <div
                style={{
                  backgroundColor: '#fff3cd',
                  border: '2px solid #ffc107',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.75rem',
                  marginBottom: '1rem'
                }}
              >
                <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '0.5rem' }}>
                  🔒 Distribution Locked
                </div>
                <div style={{ fontSize: '0.875rem', color: '#856404', marginBottom: '0.75rem' }}>
                  Unlock distribution to enable link sharing and track anonymous responses.
                </div>
                <SparkButton 
                  onClick={() => window.dispatchEvent(new CustomEvent('spark:openUnlockModal'))}
                  size="small"
                  style={{ width: '100%' }}
                >
                  UNLOCK DISTRIBUTION - $28.99
                </SparkButton>
              </div>
            )}

            <div
              style={{
                backgroundColor: '#f3f4f6',
                padding: '0.75rem',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1rem',
                opacity: unlocked ? 1 : 0.5
              }}
            >
              <input
                type="text"
                value={unlocked ? invitationLink : 'Unlock distribution to view link'}
                readOnly
                style={{
                  flexGrow: 1,
                  border: 0,
                  backgroundColor: 'transparent',
                  fontSize: '0.875rem',
                  padding: 0,
                  margin: 0,
                  color: unlocked ? '#000' : '#999'
                }}
              />

              <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '0.5rem' }}>
                <SparkButton 
                  onClick={handleCopyLink} 
                  size="small"
                  disabled={!unlocked}
                  style={{ opacity: unlocked ? 1 : 0.5 }}
                >
                  {showCopiedMessage ? 'Copied!' : 'Copy'}
                </SparkButton>
                {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
                  <SparkButton 
                    onClick={handleShare} 
                    size="small"
                    disabled={!unlocked}
                    style={{ opacity: unlocked ? 1 : 0.5 }}
                  >
                    Share
                  </SparkButton>
                )}
              </div>
            </div>

            <SparkHeading
              level="h4"
              style={{ color: '#000', textShadow: 'none', marginBottom: '0.5rem', fontSize: '1.125rem' }}
            >
              Anonymous Link Stats
            </SparkHeading>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', textAlign: 'center' }}>
              {[
                { label: 'Views', value: responses.anonymous.views, bg: 'rgba(156, 39, 176, 0.1)' },
                { label: 'Accepted', value: responses.anonymous.accepted, bg: 'rgba(74, 222, 128, 0.1)' },
                { label: 'Declined', value: responses.anonymous.declined, bg: 'rgba(255, 112, 112, 0.1)' },
                { label: 'People', value: responses.anonymous.people, bg: 'rgba(59, 130, 246, 0.1)' }
              ].map(({ label, value, bg }, i) => (
                <div
                  key={i}
                  style={{ 
                    border: '2px solid black', 
                    borderRadius: 'var(--radius-sm)', 
                    padding: '0.75rem', 
                    backgroundColor: bg,
                    opacity: unlocked ? 1 : 0.5
                  }}
                >
                  <div style={{ fontSize: '2rem', fontWeight: 900, color: '#000' }}>{value}</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#6b7280' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </SparkCard>
  );
};

export default ManageRecipientsCard;